import type { LocalizedString } from "@repo/shared/types";
import { useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { DocButton } from "@/components/ui/doc-button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFinancialSituationModels } from "@/lib/hooks/use-financial-situation-models";
import { useFlows } from "@/lib/hooks/use-flows";
import { usePortfolioCollections } from "@/lib/hooks/use-portfolio-collections";
import { useActiveRiskModels } from "@/lib/hooks/use-risk-models";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import type { Flow } from "@/lib/types/flow";
import { CloneFlowDialog } from "./clone-flow-dialog";
import { FlowDialog } from "./flow-dialog";
import { FlowTemplateDialog } from "./flow-template-dialog";
import { FlowsTable } from "./flows-table";
import { T } from "./flows-wrapper.i18n";

export function FlowsWrapper() {
  const { tenantDefaultLanguage, tenantSupportedLanguages } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const { flows, isLoading, mutate } = useFlows();
  const { models: riskModelsRaw } = useActiveRiskModels();
  const { collections: portfolioCollectionsRaw } = usePortfolioCollections();
  const { models: fsmModelsRaw } = useFinancialSituationModels();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState<Flow | null>(null);
  const [cloningFlow, setCloningFlow] = useState<Flow | null>(null);

  // QL-212 — the three input scopes ride along so the from-template dialog can
  // warn when a model captures everything on the page type the template doesn't use.
  const riskModels = riskModelsRaw.map((r: any) => ({
    id: r.id,
    name: localize(r.nameI18n as LocalizedString),
    timeHorizonScope: r.timeHorizonScope,
    riskLevelScope: r.riskLevelScope,
    adjustmentQuestionsScope: r.adjustmentQuestionsScope,
  }));

  // QL-338 — only active collections can be attached to a flow (the API
  // refuses an inactive one); an inactive collection has no usable portfolios.
  const portfolioCollections = portfolioCollectionsRaw
    .filter((c: any) => c.riskModelId !== null && c.isActive)
    .map((c: any) => ({
      id: c.id,
      name: localize(c.nameI18n as LocalizedString),
      riskModelId: c.riskModelId!,
      requiresAssetAllocation: c.requiresAssetAllocation,
      requiresTimeseries: c.requiresTimeseries,
      currency: c.currency as string,
    }));

  const financialSituationModels = fsmModelsRaw.map((m) => ({ id: m.id, name: m.name }));

  const handleCreate = () => {
    setEditingFlow(null);
    setDialogOpen(true);
  };

  const handleCreateFromTemplate = () => {
    setTemplateDialogOpen(true);
  };

  const handleEdit = (flow: Flow) => {
    setEditingFlow(flow);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingFlow(null);
    mutate();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[{ label: t.settings, href: "/settings" }, { label: t.flows }]}
        title={t.flows}
        description={t.pageDescription}
        actions={
          <DocButton docSlug="flows" variant="outline" size="sm" className="gap-2">
            {t.documentation}
          </DocButton>
        }
      />

      <div className="flex justify-end gap-2 mb-4">
        <Button variant="outline" onClick={handleCreateFromTemplate}>
          {t.fromTemplate}
        </Button>
        <Button onClick={handleCreate}>{t.createFlow}</Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <FlowsTable flows={flows} onEdit={handleEdit} onClone={setCloningFlow} />
      )}

      <FlowDialog
        open={dialogOpen}
        flow={editingFlow}
        supportedLanguages={tenantSupportedLanguages}
        defaultLanguage={tenantDefaultLanguage}
        onClose={handleDialogClose}
      />

      <CloneFlowDialog
        open={!!cloningFlow}
        flow={cloningFlow}
        onClose={() => {
          setCloningFlow(null);
          mutate();
        }}
      />

      <FlowTemplateDialog
        open={templateDialogOpen}
        onClose={() => {
          setTemplateDialogOpen(false);
          mutate();
        }}
        riskModels={riskModels}
        portfolioCollections={portfolioCollections}
        financialSituationModels={financialSituationModels}
        supportedLanguages={tenantSupportedLanguages}
        defaultLanguage={tenantDefaultLanguage}
      />
    </div>
  );
}
